import * as React from 'react';

import { parseAbsoluteToLocal } from '@internationalized/date';
import { getLocale } from '@rematter/common/locale';
import {
  Button,
  ConfirmPopoverButton,
  humanReadableDateTime,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { LucideRefreshCw, LucideTrash2 } from 'lucide-react';

import { useTranslations } from '../../../i18n/useTranslations';

import { useSidecarClient } from '~/shared/providers/libp2p';

type LogEntry = {
  level: string;
  timestamp: string;
  source: string;
  message: string;
  component: string;
};

type LogLine =
  | { kind: 'entry'; entry: LogEntry }
  | { kind: 'raw'; text: string };

const levelClassName: Record<string, string> = {
  error: 'text-failure/80',
  fatal: 'text-failure/80',
  warn: 'text-warning',
  warning: 'text-warning',
  info: 'text-primary',
  debug: 'text-teal',
};

function parseLogLines(raw: string): LogLine[] {
  return raw
    .split('\n')
    .filter(Boolean)
    .map((line): LogLine => {
      try {
        return { kind: 'entry', entry: JSON.parse(line) as LogEntry };
      } catch {
        return { kind: 'raw', text: line };
      }
    });
}

function formatTimestamp(timestampValue: string) {
  const parsedDate = new Date(timestampValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return timestampValue;
  }

  return humanReadableDateTime(parseAbsoluteToLocal(parsedDate.toISOString()), {
    locale: getLocale(),
    includeSeconds: true,
    includeYear: true,
  });
}

export type SidecarTabLogsProps = { sidecarId: string };

export const SidecarTabLogs = ({ sidecarId }: SidecarTabLogsProps) => {
  const t = useTranslations();
  const client = useSidecarClient(sidecarId);
  const toast = useToast();

  const [content, setContent] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState(false);
  const logEndRef = React.useRef<HTMLDivElement>(null);
  const abortRef = React.useRef<AbortController | null>(null);

  const startStream = React.useCallback(() => {
    if (!client) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setContent('');
    setIsLoading(true);

    (async () => {
      try {
        for await (const msg of client.getLogs(
          { tailLines: 500 },
          { signal: controller.signal },
        )) {
          setContent((prev) => prev + (msg.content ?? ''));
          setIsLoading(false);
        }
      } catch (err) {
        const isAbort =
          (err instanceof Error && err.message.includes('aborted')) ||
          (err instanceof DOMException && err.name === 'AbortError');
        const isProtocolEof =
          err instanceof Error &&
          err.message.includes('missing EndStreamResponse');

        if (!isAbort && !isProtocolEof) {
          toast.error(
            err instanceof Error
              ? err.message
              : t.hardwareView.sidecarDetailView.logs.streamErrorFallback,
          );
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, [client]);

  React.useEffect(() => {
    startStream();
    return () => {
      abortRef.current?.abort();
    };
  }, [startStream]);

  React.useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [content]);

  const handleRefresh = React.useCallback(() => {
    startStream();
  }, [startStream]);

  const handleWipe = React.useCallback(async () => {
    if (!client) return;
    try {
      abortRef.current?.abort();
      await client.wipeLogs({});
      setContent('');
      startStream();
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t.hardwareView.sidecarDetailView.logs.wipeErrorFallback,
      );
      throw err;
    }
  }, [client, startStream]);

  if (!client) {
    return (
      <div className="flex items-center justify-center p-8 text-foreground/50">
        {t.hardwareView.sidecarDetailView.logs.offline}
      </div>
    );
  }

  const lines = content ? parseLogLines(content) : [];

  const renderLogContent = () => {
    if (isLoading && !content) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="size-6 animate-spin rounded-full border-2 border-foreground/50 border-t-transparent" />
        </div>
      );
    }

    if (lines.length === 0) {
      return (
        <div className="text-center text-foreground/50">
          {t.hardwareView.sidecarDetailView.logs.noLogs}
        </div>
      );
    }

    return (
      <div className="font-mono text-sm text-white">
        {lines.map((line, i) => {
          if (line.kind === 'raw') {
            return (
              <div key={i} className="pl-8 -indent-8 text-white/70">
                {line.text}
              </div>
            );
          }

          const { entry } = line;
          return (
            <div key={i} className="pl-8 -indent-8 text-white/80">
              <span className="text-teal/80">
                [{formatTimestamp(entry.timestamp)}]
              </span>{' '}
              <span
                className={levelClassName[entry.level] ?? 'text-foreground/70'}
              >
                [{entry.level}]
              </span>{' '}
              <span className="text-olive">[{entry.component}]</span>{' '}
              {entry.message}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="@SidecarTabLogs flex max-h-full flex-col gap-4 p-4">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="default"
          icon={<LucideRefreshCw className="size-4" />}
          onPress={handleRefresh}
          isDisabled={isLoading}
        >
          {t.hardwareView.sidecarDetailView.logs.refresh}
        </Button>
        <ConfirmPopoverButton
          onConfirm={handleWipe}
          message={t.hardwareView.sidecarDetailView.logs.wipeConfirmation}
          confirmLabel={t.hardwareView.common.confirmWipe}
          cancelLabel={t.hardwareView.common.cancel}
          buttonProps={{
            variant: 'outline',
            size: 'default',
            color: 'failure',
            icon: <LucideTrash2 className="size-4" />,
          }}
        >
          {t.hardwareView.sidecarDetailView.logs.wipeLogs}
        </ConfirmPopoverButton>
      </div>

      <div className="h-full overflow-auto rounded-lg border border-neutral/20 bg-neutral/90 p-4">
        {renderLogContent()}
        <div ref={logEndRef} />
      </div>
    </div>
  );
};
